import { useEffect, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const STATUS_STYLE = {
  added: "bg-emerald-50 text-emerald-700",
  removed: "bg-rose-50 text-rose-700",
  modified: "bg-amber-50 text-amber-800",
  unchanged: "bg-zinc-100 text-zinc-500",
};

const Ops = ({ ops, side }) => (
  <>
    {ops.map((o, i) => {
      if (o.op === "equal") return <span key={i}>{o.text}</span>;
      if (side === "old" && o.op === "delete") return <del key={i} className="rounded bg-rose-100 px-0.5 text-rose-800 decoration-rose-400">{o.text}</del>;
      if (side === "new" && o.op === "insert") return <ins key={i} className="rounded bg-emerald-100 px-0.5 text-emerald-900 no-underline">{o.text}</ins>;
      return null;
    })}
  </>
);

const StepCell = ({ row, side }) => {
  const step = row[side];
  const tone = row.status === "added" && side === "new" ? "border-emerald-200 bg-emerald-50/50" : row.status === "removed" && side === "old" ? "border-rose-200 bg-rose-50/50" : "border-lasso-line bg-white";
  if (!step) return <div className="rounded-xl border border-dashed border-lasso-line p-4 text-xs italic text-zinc-400">{side === "old" ? "Not in this version" : "Removed in this version"}</div>;
  const modified = row.status === "modified";
  return (
    <div className={`rounded-xl border p-4 ${tone}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Step {row[`${side}_index`] + 1}</p>
      <p className="mt-1 font-display text-sm font-bold">{modified ? <Ops ops={row.title_ops} side={side} /> : step.title}</p>
      <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-zinc-700">{modified ? <Ops ops={row.detail_ops} side={side} /> : step.detail}</p>
    </div>
  );
};

const FieldRow = ({ label, field }) => (
  <div className="grid gap-3 md:grid-cols-2">
    {["old", "new"].map((side) => (
      <div key={side} className="rounded-xl border border-amber-200 bg-white p-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
        <p className="mt-1 text-sm leading-relaxed"><Ops ops={field.ops} side={side} /></p>
      </div>
    ))}
  </div>
);

export const SopDiffDialog = ({ sopId, range, onClose }) => {
  const [diff, setDiff] = useState(null);
  const [error, setError] = useState(null);
  const [showUnchanged, setShowUnchanged] = useState(false);

  useEffect(() => {
    if (!range) return;
    setDiff(null);
    setError(null);
    api.get(`/sops/${sopId}/diff`, { params: { from_version: range[0], to_version: range[1] } }).then((r) => setDiff(r.data)).catch((e) => setError(formatApiError(e)));
  }, [sopId, range]);

  const steps = diff ? diff.steps.filter((r) => showUnchanged || r.status !== "unchanged") : [];
  return (
    <Dialog open={!!range} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto" data-testid="sop-diff-dialog">
        <DialogHeader><DialogTitle className="font-display">Compare v{range?.[0]} → v{range?.[1]}</DialogTitle></DialogHeader>
        {error && <p className="text-sm text-rose-600" data-testid="sop-diff-error">{error}</p>}
        {!diff && !error && <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading comparison…</div>}
        {diff && (
          <div className="space-y-6">
            {diff.change_summary.length > 0 && (
              <div className="rounded-xl bg-lasso-tint p-4" data-testid="sop-diff-summary">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-lasso-deep"><Sparkles className="h-3.5 w-3.5" /> What changed</p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-lasso-navy">{diff.change_summary.map((b, i) => <li key={i}>{b}</li>)}</ul>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="sop-diff-counts">
              {["added", "removed", "modified", "unchanged"].map((k) => <span key={k} className={`rounded-full px-2.5 py-1 font-semibold ${STATUS_STYLE[k]}`}>{diff.counts[k]} {k}</span>)}
              <label className="ml-auto flex cursor-pointer items-center gap-2 text-zinc-500">
                <input type="checkbox" checked={showUnchanged} onChange={(e) => setShowUnchanged(e.target.checked)} data-testid="sop-diff-show-unchanged" /> Show unchanged steps
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3 border-b border-lasso-line pb-2 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
              <span>v{diff.from_version} (older)</span><span>v{diff.to_version} (newer)</span>
            </div>
            {["title", "summary", "purpose"].filter((f) => diff.fields[f].changed).map((f) => <FieldRow key={f} label={f} field={diff.fields[f]} />)}
            <div className="space-y-3" data-testid="sop-diff-steps">
              {steps.map((r, i) => (
                <div key={i} data-testid={`sop-diff-step-${r.status}-${i}`}>
                  <span className={`mb-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                  <div className="grid gap-3 md:grid-cols-2"><StepCell row={r} side="old" /><StepCell row={r} side="new" /></div>
                </div>
              ))}
              {steps.length === 0 && <p className="text-sm text-muted-foreground">No step changes{showUnchanged ? "" : " — toggle “Show unchanged steps” to see them all"}.</p>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
